import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { X, ArrowRight } from "lucide-react";

export default function Compare() {
  const [peptides, setPeptides] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => { api.get("/peptides").then(({ data }) => setPeptides(data)).catch(() => {}); }, []);

  const toggle = (slug) => {
    setSelected((s) => s.includes(slug) ? s.filter((x) => x !== slug) : s.length < 4 ? [...s, slug] : s);
  };

  const sel = peptides.filter((p) => selected.includes(p.slug));
  const fields = [
    ["Type", (p) => p.peptide_type], ["Family", (p) => p.peptide_family],
    ["Molecular Weight", (p) => p.molecular_weight ? `${p.molecular_weight} Da` : "—"],
    ["Sequence Length", (p) => p.sequence_length || "—"],
    ["Categories", (p) => (p.categories || []).join(", ")],
    ["Storage", (p) => p.storage], ["Solubility", (p) => p.solubility],
    ["Compliance", (p) => p.compliance_status],
  ];

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">Comparison engine</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-4">Compare Peptides</h1>
      <p className="text-slate-400 text-sm mb-8">Select up to 4 peptides to compare side by side.</p>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="compare-selector">
        {peptides.map((p) => (
          <button key={p.slug} onClick={() => toggle(p.slug)} data-testid={`compare-select-${p.slug}`}
            className={`px-3 py-1.5 rounded-full text-xs font-mono transition-all border ${selected.includes(p.slug) ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/10 bg-white/5 text-slate-400 hover:border-cyan-400/30"}`}>
            {p.name}
          </button>
        ))}
      </div>

      {sel.length === 0 && <div className="glass rounded-2xl p-12 text-center text-slate-500 text-sm">Select peptides above to begin comparing.</div>}

      {sel.length > 0 && (
        <div className="overflow-x-auto" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-3 text-xs font-mono uppercase tracking-widest text-slate-500 w-40">Field</th>
                {sel.map((p) => (
                  <th key={p.slug} className="p-3 text-left min-w-[200px]">
                    <div className="glass rounded-2xl p-4 relative">
                      <button onClick={() => toggle(p.slug)} className="absolute top-2 right-2 text-slate-500 hover:text-rose-400"><X size={13} /></button>
                      <div className="font-serif text-xl text-white">{p.name}</div>
                      <div className="text-[10px] font-mono text-slate-500 mt-1 truncate">{p.scientific_name}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(([label, fn]) => (
                <tr key={label} className="border-t border-white/5">
                  <td className="p-3 text-xs font-mono uppercase tracking-widest text-slate-500">{label}</td>
                  {sel.map((p) => (
                    <td key={p.slug} className="p-3 text-xs font-mono text-slate-300 align-top">{fn(p) || "—"}</td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-white/5">
                <td className="p-3" />
                {sel.map((p) => (
                  <td key={p.slug} className="p-3">
                    <Link to={`/peptides/${p.slug}`} className="inline-flex items-center gap-1 text-xs font-mono text-cyan-300 hover:text-cyan-200">
                      View <ArrowRight size={11} />
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
